import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const cacheFirstFetch = async (apiFetch, cacheUrl, liveUrl, options = {}) => {
  // Try cache first - returns 204 No Content if cache not available (not an error)
  try {
    const cacheRes = await apiFetch(cacheUrl, options);
    if (cacheRes.ok && cacheRes.status !== 204) {
      try {
        const cacheData = await cacheRes.json();
        if (cacheData && cacheData.data) {
          return cacheData.data;
        }
      } catch {
        // Cache response had invalid/empty JSON body — fall through to live
      }
    }
  } catch (catchError) {
    if (catchError.name === 'AbortError') return null;
    Sentry.captureException(catchError);
    // Silently fail and try live endpoint
  }

  // Fallback to live endpoint with a 30-second timeout to prevent hanging
  try {
    const timeoutMs = 30000;
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const combinedSignal = options.signal
      ? AbortSignal.any([options.signal, timeoutSignal])
      : timeoutSignal;
    const liveRes = await apiFetch(liveUrl, { ...options, signal: combinedSignal });
    if (liveRes.ok) {
      try {
        const liveData = await liveRes.json();
        return liveData.data;
      } catch {
        // Live response had invalid/empty JSON body
        return null;
      }
    }
  } catch (catchError) {
    if (catchError.name === 'AbortError' || catchError.name === 'TimeoutError') return null;
    Sentry.captureException(catchError);
    // Both cache and live failed
  }

  return null;
};
const formatNumber = (n) => (Number(n) || 0).toLocaleString('en-US').replace(/,/g,' ');
const formatPercent = (n) => `${((Number(n)||0)*100).toFixed(1)}%`;
const percentChange = (c,p) => { const C=Number(c), P=Number(p); if(!isFinite(C)||!isFinite(P)||P===0) return NaN; return ((C-P)/P)*100; };
const parseDurationMinutes = (v) => { if (v==null) return NaN; if (typeof v==='number') return Math.max(0,v)/60000; const s=String(v); if(/^P/i.test(s)){ const d=parseFloat(/(?:(\d+)D)/i.exec(s)?.[1]||'0'); const h=parseFloat(/T(?:(\d+)H)/i.exec(s)?.[1]||'0'); const m=parseFloat(/(?:(\d+)M)/i.exec(s)?.[1]||'0'); return (d*24*60)+(h*60)+m; } const m2=s.match(/^(?:(\d+)\.)?(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/); if(m2){ const days=Number(m2[1]||0), hours=Number(m2[2]||0), minutes=Number(m2[3]||0); return days*24*60+hours*60+minutes;} return NaN; };
const formatDuration = (v) => { if(v==null && v!==0) return '-'; const mins=parseDurationMinutes(v); if(!isFinite(mins)) return String(v); const m=Math.floor(mins%60); const h=Math.floor((mins/60)%24); const d=Math.floor(mins/1440); if(d>0) return `${d}d ${h}h ${m}m`; if(h>0) return `${h}h ${m}m`; return `${Math.floor(mins)}m`; };

const InlineIndicator = ({ value, improve }) => { if(!isFinite(value) || value===0) return null; const up = value>0; const good = improve ? up : !up; const color = good ? 'green' : 'red'; const arrow = up ? '\u2191' : '\u2193'; return <span style={{ color, fontWeight:600, marginLeft:4 }}>{arrow}{Math.abs(value).toFixed(1)}%</span>; };

// Deployment frequency mapping
function mapDeployFreqScore(freq){
  if (freq == null) return null;
  if (freq >= 14) return 100;   // many per day
  if (freq >= 7)  return 95;    // daily
  if (freq >= 3.5) return 90;   // every couple of days
  if (freq >= 1)  return 80;    // weekly
  if (freq >= 0.25) return 60;  // monthly
  if (freq >= 0.083) return 40; // quarterly
  if (freq > 0) return 25;      // rarer
  return 0;
}
// Lead time scoring: <=1d 100, 7d 0 linear
function leadTimeScore(mins){ if(!isFinite(mins)) return null; const best=24*60, worst=7*24*60; if(mins<=best) return 100; if(mins>=worst) return 0; return 100*(1-(mins-best)/(worst-best)); }
// MTTR scoring: <=60m 100, 60m->24h linear to 80, 24h->7d linear to 0
function mttrScore(mins){ if(!isFinite(mins)) return null; const fast=60, day=24*60, worst=7*24*60; if(mins<=fast) return 100; if(mins<=day) return 80 + ( (day - mins)/(day-fast) )*20; if(mins>=worst) return 0; return ( (worst - mins)/(worst-day) )*80; }

const GithubActivitySummary = ({ owner, repo }) => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading,setLoading]=useState(true); 
  const [error,setError]=useState(''); 
  const [data,setData]=useState(null); 
  const [compare,setCompare]=useState(null);
  
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  const previous = compare?.previous; 
  const prior = compare?.prior; 
  const prevDora = previous?.dora; 
  const priorDora = prior?.dora;

  // Composite Development Velocity & Reliability Score (Deploy 25%, CFR 25%, Lead 30%, MTTR 20%)
  const healthScore = useMemo(() => {
    if (!prevDora) return null;
    const deployScore = mapDeployFreqScore(prevDora.deploymentFrequencyPerWeek ?? null);
    const cfrRaw = prevDora.changeFailureRate;
    let cfrScore = null; if (cfrRaw != null){ if (cfrRaw <= 0.15) cfrScore=100; else if (cfrRaw <= 0.30) cfrScore=80; else if (cfrRaw <= 0.45) cfrScore=55; else cfrScore=30; }
    const leadMins = parseDurationMinutes(prevDora.leadTimeP50); let leadComponent = leadTimeScore(leadMins);
    const mttrMins = parseDurationMinutes(prevDora.meanTimeToRestore); let mttrComponent = mttrScore(mttrMins);
    let leadImputed=false, mttrImputed=false;
    if (leadComponent==null && (deployScore!=null || cfrScore!=null || mttrComponent!=null)) { leadComponent=50; leadImputed=true; }
    if (mttrComponent==null && (deployScore!=null || cfrScore!=null || leadComponent!=null)) { mttrComponent=50; mttrImputed=true; }
    if (deployScore==null && cfrScore==null && leadComponent==null && mttrComponent==null) return null;
    let composite = (deployScore??50)*0.25 + (cfrScore??50)*0.25 + (leadComponent??50)*0.30 + (mttrComponent??50)*0.20;
    const missingCore = deployScore==null || cfrScore==null || leadComponent==null || mttrComponent==null || leadImputed || mttrImputed;
    if (missingCore && composite>85) composite=85; // cap on partial data
    return Math.round(composite);
  }, [prevDora]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 90) return { text: 'Elite', color: '#2e7d32', icon: '✓' };
    if (score >= 80) return { text: 'High', color: '#388e3c', icon: '✓' };
    if (score >= 60) return { text: 'Medium', color: '#fbc02d', icon: '⚠' };
    if (score >= 40) return { text: 'Low', color: '#ff5722', icon: '⚠' };
    return { text: 'Needs Improvement', color: '#d32f2f', icon: '✗' };
  };

  const chartData = useMemo(()=> { 
    if(!previous||!prior) return null; 
    const a=Array.isArray(previous.dailyCommits)?previous.dailyCommits:[]; 
    const b=Array.isArray(prior.dailyCommits)?prior.dailyCommits:[]; 
    const days=Math.max(a.length,b.length); 
    return Array.from({ length:days },(_,i)=>({ 
      day: (()=>{ 
        try{ 
          const fromPrev=previous?.fromUtc?new Date(previous.fromUtc):null; 
          if(fromPrev){ 
            const d=new Date(fromPrev); 
            d.setUTCDate(d.getUTCDate()+i); 
            return d.getUTCDate(); 
          } 
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* Date parsing may fail for invalid dates */
        }
        return i+1; 
      })(), 
      prevCommits:a[i]??0, 
      priorCommits:b[i]??0 
    })); 
  }, [previous,prior]);

  useEffect(()=>{ 
    let ignore=false;
    const controller = new AbortController();
    (async()=>{ 
      setLoading(true); 
      setError(''); 
      try { 
        const q=new URLSearchParams(); 
        if(owner) q.set('owner',owner); 
        if(repo) q.set('repo',repo);
        const fetchOpts = { signal: controller.signal };
        const [cur, cmp] = await Promise.all([
          cacheFirstFetch(apiFetch, `/api/github/repo-summary/cache?${q}`, `/api/github/repo-summary?${q}`, fetchOpts),
          cacheFirstFetch(apiFetch, `/api/github/repo-summary/compare/cache?${q}`, `/api/github/repo-summary/compare?${q}`, fetchOpts),
        ]);
        if(!ignore){ 
          setData(cur); 
          setCompare(cmp);
        } 
      } catch(e){
        if (e.name === 'AbortError') return;
        Sentry.captureException(e);
        if(!ignore) setError(e.message);
      } finally { 
        if(!ignore) setLoading(false);
      } 
    })(); 
    return ()=>{ ignore=true; controller.abort(); }; 
  }, [owner,repo]); // eslint-disable-line

  if (loading) return <HexagonLoader/>; 
  if (error) return <div style={{ color:'red' }}>{error}</div>; 
  if(!data) return null; 
  if(!chartData || chartData.length===0) return <div className="dashboard-card">No commit data.</div>;

  const comparisonRows = previous && prior ? [
    { label:'Commits', key:'commitCount' },
    { label:'PRs Opened', key:'pullRequestsOpened' },
    { label:'PRs Merged', key:'pullRequestsMerged' },
    { label:'Issues Opened', key:'issuesOpened' },
    { label:'Issues Closed', key:'issuesClosed' },
    ...(prevDora && priorDora ? [
      { label:'Deployments', key:'dora.deployments', accessor:o=>o?.dora?.deployments },
      { label:'Deploy Freq (/wk)', key:'dora.deploymentFrequencyPerWeek', accessor:o=>o?.dora?.deploymentFrequencyPerWeek, formatter:v=>Number(v||0).toFixed(2) },
      { label:'Lead Time P50', key:'dora.leadTimeP50', accessor:o=>o?.dora?.leadTimeP50, formatter:formatDuration, computePercent:(cv,pv)=>{ const c=parseDurationMinutes(cv), p=parseDurationMinutes(pv); return (p===0||!isFinite(c)||!isFinite(p))?0:((c-p)/p)*100; }, isLowerBetter:true },
      { label:'Lead Time P95', key:'dora.leadTimeP95', accessor:o=>o?.dora?.leadTimeP95, formatter:formatDuration, computePercent:(cv,pv)=>{ const c=parseDurationMinutes(cv), p=parseDurationMinutes(pv); return (p===0||!isFinite(c)||!isFinite(p))?0:((c-p)/p)*100; }, isLowerBetter:true },
      { label:'Change Failure Rate', key:'dora.changeFailureRate', accessor:o=>o?.dora?.changeFailureRate, formatter:formatPercent, computePercent:(cv,pv)=> ((Number(cv)||0)-(Number(pv)||0))*100, isLowerBetter:true },
      { label:'MTTR', key:'dora.meanTimeToRestore', accessor:o=>o?.dora?.meanTimeToRestore, formatter:formatDuration, computePercent:(cv,pv)=>{ const c=parseDurationMinutes(cv), p=parseDurationMinutes(pv); return (p===0||!isFinite(c)||!isFinite(p))?0:((c-p)/p)*100; }, isLowerBetter:true },
    ]:[])
  ]:[];
  
  const cfrDeltaPts = (prevDora && priorDora) ? ((Number(prevDora.changeFailureRate)||0) - (Number(priorDora.changeFailureRate)||0))*100 : 0;
  const scoreInterp = getScoreInterpretation(healthScore);
  const deployFreq = prevDora?.deploymentFrequencyPerWeek || 0;
  const cfr = (prevDora?.changeFailureRate || 0) * 100;
  const leadTimeMins = parseDurationMinutes(prevDora?.leadTimeP50);
  const leadTimeDays = isFinite(leadTimeMins) ? leadTimeMins / 1440 : null;
  const leadTimeMissing = prevDora && prevDora.leadTimeP50 == null;
  const mttrMins = parseDurationMinutes(prevDora?.meanTimeToRestore);
  const mttrMissing = prevDora && prevDora.meanTimeToRestore == null;

  const llmProxyText = [
    `Development Velocity Score: ${healthScore}/100`,
    `Commits: ${formatNumber(data.commitCount)}`,
    `Deployments: ${formatNumber(data.dora?.deployments)} (${deployFreq.toFixed(2)}/week)`,
    `Change Failure Rate: ${cfr.toFixed(1)}%`,
    `Lead Time: ${formatDuration(prevDora?.leadTimeP50)}`,
    `MTTR: ${formatDuration(prevDora?.meanTimeToRestore)}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper github-activity-summary">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom: 12 }}>Development Velocity & Quality</h2>

      {/* Executive Summary Banner */}
      {prevDora && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
                {healthScore != null ? healthScore : '–'}
                <span className="exec-banner-suffix">/ 100</span>
              </div>
              <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
              <div className="exec-banner-sublabel">DevOps Performance (DORA)</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> Your development team delivered <strong>{formatNumber(data.dora.deployments)} deployments</strong> in {data.periodLabel} ({deployFreq.toFixed(1)}/week) with <strong>{cfr.toFixed(0)}% change failure rate</strong>{leadTimeDays!=null && <> and <strong>{formatDuration(prevDora.leadTimeP50)} median lead time</strong></>}{mttrMins && isFinite(mttrMins) && <> and <strong>{formatDuration(prevDora.meanTimeToRestore)} MTTR</strong></>}.{leadTimeMissing && ' Lead time instrumentation missing – provisional neutral value (50) used (score capped).'}{mttrMissing && ' MTTR instrumentation missing – provisional neutral value (50) used (score capped).'}
                {deployFreq >= 7 && cfr <= 15 && leadTimeDays != null && leadTimeDays <= 1 && mttrMins <= 60 && ' Elite performance across speed, quality & resilience.'}
                {deployFreq < 1 && <span style={{ color: '#ff5722', fontWeight: 600 }}> Deployment cadence below weekly – consider smaller, more frequent releases.</span>}
                {cfr > 30 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Elevated failure rate – strengthen testing & release gates.</span>}
                {leadTimeDays != null && leadTimeDays > 7 && <span style={{ color: '#ffa000', fontWeight: 600 }}> Long lead time – investigate cycle bottlenecks (review / QA / deploy).</span>}
                {isFinite(mttrMins) && mttrMins > 24*60 && <span style={{ color: '#ffa000', fontWeight: 600 }}> Slow restore time – improve rollback / incident response automation.</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ margin: '-8px 0 12px 0', fontSize:11, color:'#777' }}>
        Repository: <strong>{owner}/{repo}</strong> • Period: {data.periodLabel}
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card metric-card--md" title="Code contributions to the repository">
          <div className="metric-label">Commits</div>
          <div className="metric-value--md" style={{ fontWeight:700 }}>{formatNumber(data.commitCount)}</div>
        </div>
        <div className="dashboard-card metric-card" title="Pull requests opened and successfully merged">
          <div className="metric-label">PRs Opened / Merged</div>
          <div className="metric-value--sm">{formatNumber(data.pullRequestsOpened)} / {formatNumber(data.pullRequestsMerged)}</div>
        </div>
        <div className="dashboard-card metric-card" title="Issues created and resolved">
          <div className="metric-label">Issues Opened / Closed</div>
          <div className="metric-value--sm">{formatNumber(data.issuesOpened)} / {formatNumber(data.issuesClosed)}</div>
        </div>
        {data?.dora && (
          <>
            <div className="dashboard-card metric-card" title="Number of production deployments">
              <div className="metric-label">Deployments</div>
              <div className="metric-value--sm" style={{ color: deployFreq >= 7 ? '#2e7d32' : deployFreq >= 1 ? '#388e3c' : deployFreq >= 0.25 ? '#fbc02d' : '#ffa000' }}>
                {formatNumber(data.dora.deployments)}
              </div>
              <div className="metric-subtitle" style={{ marginTop: 4 }}>{deployFreq.toFixed(2)}/week</div>
            </div>
            <div className="dashboard-card metric-card" title="Percentage of deployments causing failures (lower is better)">
              <div className="metric-label">Change Failure Rate</div>
              <div className="metric-value--sm" style={{ color: cfr <= 15 ? '#2e7d32' : cfr <= 30 ? '#388e3c' : cfr <= 45 ? '#fbc02d' : '#d32f2f' }}>
                {cfr.toFixed(0)}%
              </div>
              {prevDora && priorDora && cfrDeltaPts !== 0 && (
                <div className="metric-delta" style={{ color: cfrDeltaPts <= 0 ? 'green' : '#d32f2f' }}>
                  {cfrDeltaPts <= 0 ? '↓' : '↑'} {Math.abs(cfrDeltaPts).toFixed(1)}%
                </div>
              )}
            </div>
            <div className="dashboard-card metric-card" title="Median time from commit to production deployment (lower is better)">
              <div className="metric-label">Lead Time (P50)</div>
              <div className="metric-value--text" style={{ fontWeight:700, marginTop: 4, color: leadTimeDays != null ? (leadTimeDays <= 1 ? '#2e7d32' : leadTimeDays <= 7 ? '#388e3c' : leadTimeDays <= 30 ? '#fbc02d':'#ffa000') : '#666' }}>
                {formatDuration(prevDora.leadTimeP50) || (leadTimeMissing? '–*':'–')}
              </div>
              <div className="metric-subtitle" style={{ marginTop: 4 }}>{leadTimeMissing? 'Instrumentation missing':'Commit to deploy'}</div>
            </div>
            <div className="dashboard-card metric-card" title="Average time to restore after failed deployment (lower is better)">
              <div className="metric-label">MTTR</div>
              <div className="metric-value--text" style={{ fontWeight:700, marginTop: 4, color: isFinite(mttrMins) ? (mttrMins <= 60 ? '#2e7d32' : mttrMins <= 24*60 ? '#388e3c' : mttrMins <= 7*24*60 ? '#fbc02d' : '#ffa000') : '#666' }}>
                {formatDuration(prevDora.meanTimeToRestore) || (mttrMissing? '–*':'–')}
              </div>
              <div className="metric-subtitle" style={{ marginTop: 4 }}>{mttrMissing? 'Instrumentation missing':'Failed deploy to recover'}</div>
            </div>
          </>
        )}
      </div>

      {/* Daily Commits Chart */}
      <div className="dashboard-card chart-card">
        <h4 className="chart-title">Daily Development Activity</h4>
        <div className="chart-subtitle">Commit frequency over time - indicates team productivity</div>
        <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={150} minHeight={150}>
          <LineChart data={chartData} margin={{ left:0, right:0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }} />
            <YAxis tickFormatter={formatNumber} />
            <Tooltip formatter={(v)=>[formatNumber(v),'Commits']} />
            <Legend />
            <Line type="monotone" dataKey="prevCommits" name={previous?.periodLabel} stroke={primaryColor} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
            <Line type="monotone" dataKey="priorCommits" name={prior?.periodLabel} stroke={secondaryColor} strokeDasharray="6 3" strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Comparison Table */}
      {previous && prior && (
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Month-over-Month Performance</h4>
          <div className="chart-subtitle">Comparing {previous.periodLabel} vs {prior.periodLabel}</div>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {/* All four columns get an explicit width hint. Print's
                    table-layout:auto renderer doesn't reliably size columns
                    left fully unconstrained (the two period-value columns
                    below used to have no width at all) - it was rendering
                    them collapsed to near-zero, with header/value text from
                    all three left-hand columns stacking on top of each
                    other. Giving every column a width hint (even a rough
                    one) makes the layout deterministic instead. */}
                <th style={{ textAlign:'left', padding: '6px', width: 160 }}>Metric</th>
                <th style={{ padding: '6px', textAlign: 'right', width: 130 }}>{prior.periodLabel}</th>
                <th style={{ padding: '6px', textAlign: 'right', width: 130 }}>{previous.periodLabel}</th>
                <th style={{ padding: '6px', textAlign: 'right', width: 80 }}>Change</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, idx)=>{ 
                const pv=row.accessor?row.accessor(prior):prior[row.key]; 
                const cv=row.accessor?row.accessor(previous):previous[row.key]; 
                const fmt=row.formatter||formatNumber; 
                const delta=row.computePercent?row.computePercent(cv,pv):percentChange(cv,pv); 
                const improve=row.isLowerBetter? delta<=0 : delta>=0; 
                const isDoraBoundary = row.key === 'dora.deployments' && idx > 0;
                return (
                  <tr key={row.key} style={{ borderTop: isDoraBoundary ? '2px solid #ccc' : '1px solid #eee' }}>
                    <td style={{ fontWeight:600, padding: '6px', textAlign: 'left' }}>{row.label}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{fmt(pv)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600 }}>{fmt(cv)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight:600 }}><InlineIndicator value={isFinite(delta)?delta:0} improve={improve} /></td>
                  </tr>
                ); 
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding DORA Metrics:</strong>
        <ul>
          <li><strong>Development Velocity & Reliability Score:</strong> Deployment Frequency (25%), Change Failure Rate (25%), Lead Time P50 (30%), MTTR (20%). Missing core metrics cap score (&gt;85) and provisional neutral (50) used where instrumentation absent (*).</li>
          <li><strong>Deployment Frequency:</strong> Many/day (≥14/wk) 100, Daily (≥7) ~95, Every couple of days (≥3.5) 90, Weekly (≥1) 80, Monthly (≥0.25) 60, Quarterly (≥0.083) 40, Rare 25.</li>
          <li><strong>Lead Time for Changes (P50):</strong> &lt;=1 day 100, 1–7 days linear to 0 at 7 days.</li>
          <li><strong>Change Failure Rate:</strong> % of deployments causing production failure. &lt;15% 100, 15–30% 80, 30–45% 55, &gt;45% 30.</li>
          <li><strong>MTTR (Mean Time To Restore):</strong> &lt;=60m 100, 60m–24h slopes to 80, 24h–7d slopes to 0 (improves resilience focus).</li>
          <li><strong>Instrumentation gaps:</strong> A provisional neutral value (50) is used where a key metric (e.g. lead time, MTTR) is missing.</li>
        </ul>
      </div>
    </div>
  );
};

export default GithubActivitySummary;
